import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Checkbox, NumericTextBox } from "@progress/kendo-react-inputs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { chevronDownIcon } from "@progress/kendo-svg-icons";
import { toast } from "react-toastify";

import infoIcon from "assets/CaseFileIcons/info-icon.png";
import { Tile } from "components/UIComponents/Tile";
import { TileWrapper } from "components/UIComponents/TileWrapper";
import SubmitButton from "components/common/SubmitButton";
import { fetchConsultantRefs } from "services/supplierService";
import { getPaymentProfile, updatePaymentProfile } from "services/mattersService";

const DRAFT_KEY = "draft";

const REFERRER_INFO =
  "A referrer is a person or supplier who receives the agreed commission on every bill for this matter, even if they are not included on the bill.";

const blankRow = (key) => ({
  key,
  supplierRef: null,
  paymentPercentage: null,
  referrer: false,
});

const toRows = (data) =>
  (data || [])
    .filter((item) => item.active !== false)
    .map((item, index) => ({
      key: item.matterPaymentProfileId ?? `saved-${index}`,
      supplierRef: item.supplierRef ?? null,
      paymentPercentage: item.paymentPercentage ?? null,
      referrer: item.referrer === true,
    }));

const NameCell = ({ dataItem, tdProps, onConsultantChange }) => (
  <td {...tdProps}>
    <ComboBox
      data={dataItem.consultantOptions}
      textField="display"
      dataItemKey="supplierRef"
      svgIcon={chevronDownIcon}
      placeholder="Select Consultant"
      style={{ width: "280px" }}
      value={dataItem.consultant}
      onChange={(event) => onConsultantChange(dataItem, event.value)}
    />
  </td>
);

const ReferrerHeaderCell = (props) => (
  <HeaderThElement {...props.thProps}>
    <span className="d-flex align-items-center">
      {props.title}
      <Tooltip
        anchorElement="target"
        position="top"
        showOption={{ hover: true, click: true }}
        tooltipStyle={{ maxWidth: "320px" }}>
        <img
          className="ms-1 cursor-pointer"
          src={infoIcon}
          title={REFERRER_INFO}
          style={{ height: "14px", width: "14px" }}
          alt="info"
        />
      </Tooltip>
    </span>
  </HeaderThElement>
);

const ReferrerCell = ({ dataItem, tdProps, onReferrerChange }) => (
  <td {...tdProps}>
    <div className="text-center w-50">
      <Checkbox
        checked={dataItem.referrer === true}
        disabled={!dataItem.supplierRef}
        onChange={(event) => onReferrerChange(dataItem, event.value)}
      />
    </div>
  </td>
);

const PercentageCell = ({ dataItem, tdProps, onPercentageChange }) => (
  <td {...tdProps}>
    <NumericTextBox
      value={dataItem.paymentPercentage ?? null}
      format={{ maximumFractionDigits: 2 }}
      min={0}
      max={100}
      spinners={false}
      disabled={!dataItem.supplierRef}
      className="right-align"
      style={{ width: "90px" }}
      onChange={(event) => onPercentageChange(dataItem, event.value)}
    />
  </td>
);

const PaymentProfileTab = ({ matterId }) => {
  const [consultants, setConsultants] = useState([]);
  const [rows, setRows] = useState([]);
  const [removedRefs, setRemovedRefs] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const nextKey = useRef(0);

  useEffect(() => {
    let cancelled = false;
    fetchConsultantRefs().then((data) => {
      if (!cancelled) setConsultants(data || []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!matterId) return undefined;
    let cancelled = false;
    getPaymentProfile(matterId).then((response) => {
      if (cancelled) return;
      setRows(toRows(response?.data?.data));
      setRemovedRefs([]);
    });
    return () => {
      cancelled = true;
    };
  }, [matterId]);

  const consultantByRef = useMemo(
    () => new Map((consultants || []).map((item) => [item.supplierRef, item])),
    [consultants],
  );

  const gridRows = useMemo(() => {
    const all = [...rows, blankRow(DRAFT_KEY)];
    return all.map((row) => ({
      ...row,
      consultant: consultantByRef.get(row.supplierRef) ?? null,
      consultantOptions: consultants.filter(
        (item) =>
          item.supplierRef === row.supplierRef ||
          !all.some(
            (other) =>
              other.key !== row.key && other.supplierRef === item.supplierRef,
          ),
      ),
    }));
  }, [rows, consultants, consultantByRef]);

  const handleConsultantChange = useCallback((row, consultant) => {
    const supplierRef = consultant?.supplierRef ?? null;

    if (row.key === DRAFT_KEY) {
      if (!supplierRef) return;
      nextKey.current += 1;
      setRows((current) => [
        ...current,
        { ...blankRow(`new-${nextKey.current}`), supplierRef },
      ]);
      return;
    }

    if (!supplierRef) {
      if (row.supplierRef) {
        setRemovedRefs((current) => [...current, row.supplierRef]);
      }
      setRows((current) => current.filter((item) => item.key !== row.key));
      return;
    }

    setRows((current) =>
      current.map((item) =>
        item.key === row.key ? { ...item, supplierRef } : item,
      ),
    );
  }, []);

  const handlePercentageChange = useCallback((row, value) => {
    if (row.key === DRAFT_KEY) return;
    setRows((current) =>
      current.map((item) =>
        item.key === row.key ? { ...item, paymentPercentage: value } : item,
      ),
    );
  }, []);

  const handleReferrerChange = useCallback((row, value) => {
    if (row.key === DRAFT_KEY) return;
    setRows((current) =>
      current.map((item) =>
        item.key === row.key ? { ...item, referrer: value === true } : item,
      ),
    );
  }, []);

  const handleSave = async () => {
    if (!matterId) return;
    const selectedRefs = new Set(rows.map((row) => row.supplierRef));
    const payload = [
      ...rows.map((row) => ({
        supplierRef: row.supplierRef,
        paymentPercentage: row.paymentPercentage ?? 0,
        referrer: row.referrer === true,
        active: true,
      })),
      ...removedRefs
        .filter((supplierRef) => !selectedRefs.has(supplierRef))
        .map((supplierRef) => ({
          supplierRef,
          paymentPercentage: 0,
          referrer: false,
          active: false,
        })),
    ];

    setIsSaving(true);
    try {
      const response = await updatePaymentProfile(matterId, payload);
      setRows(toRows(response?.data?.data));
      setRemovedRefs([]);
      toast.success("Payment profile updated successfully");
    } finally {
      setIsSaving(false);
    }
  };

  const nameCell = useMemo(
    () => (props) => (
      <NameCell {...props} onConsultantChange={handleConsultantChange} />
    ),
    [handleConsultantChange],
  );

  const referrerCell = useMemo(
    () => (props) => (
      <ReferrerCell {...props} onReferrerChange={handleReferrerChange} />
    ),
    [handleReferrerChange],
  );

  const percentageCell = useMemo(
    () => (props) => (
      <PercentageCell {...props} onPercentageChange={handlePercentageChange} />
    ),
    [handlePercentageChange],
  );

  return (
    <TileWrapper>
      <div className="d-flex flex-column dialog-tab-height">
        <Tile className="px-4 pt-2 pb-3 d-flex flex-column flex-grow-1 overflow-hidden">
          <div className="mt-2 flex-grow-1" style={{ minHeight: 0 }}>
            <Grid data={gridRows} dataItemKey="key" style={{ height: "100%" }}>
              <GridColumn
                field="supplierRef"
                title="Name"
                width="320px"
                cells={{ data: nameCell }}
              />
              <GridColumn
                field="referrer"
                title="Referrer"
                width="150px"
                cells={{ headerCell: ReferrerHeaderCell, data: referrerCell }}
              />
              <GridColumn
                field="paymentPercentage"
                title="Percentage"
                cells={{ data: percentageCell }}
              />
            </Grid>
          </div>

          <div className="d-flex justify-content-end mt-3">
            <SubmitButton
              type="button"
              className="bgBtnSecondary fw-bold"
              style={{ minWidth: "11.5rem" }}
              onClick={handleSave}
              isLoading={isSaving}
              loaderClassName="">
              Update Payment Profile
            </SubmitButton>
          </div>
        </Tile>
      </div>
    </TileWrapper>
  );
};

export default PaymentProfileTab;
